import { useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Shield, CalendarCheck } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { trackCTAClick } from "@/hooks/usePageTracking";
import defaultHeroImage from "@/assets/hero-image.jpg";

const Hero = () => {
  const { t } = useLanguage();
  const { cms, loading } = useCMS();
  const heroImage = cms.images.hero || defaultHeroImage;

  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  if (loading) {
    return (
      <section className="relative min-h-[90vh] flex items-center pt-16 overflow-hidden bg-muted animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-r from-muted-foreground/10 via-muted-foreground/5 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl space-y-6">
            <div className="h-8 w-48 bg-muted-foreground/10 rounded-full" />
            <div className="space-y-3">
              <div className="h-12 w-full bg-muted-foreground/10 rounded-lg" />
              <div className="h-12 w-3/4 bg-muted-foreground/10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <div className="h-5 w-full bg-muted-foreground/10 rounded" />
              <div className="h-5 w-2/3 bg-muted-foreground/10 rounded" />
            </div>
            <div className="flex gap-4 pt-2">
              <div className="h-12 w-40 bg-muted-foreground/10 rounded-lg" />
              <div className="h-12 w-40 bg-muted-foreground/10 rounded-lg" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative min-h-[90vh] flex items-center pt-16 overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imageY }}>
        <img src={heroImage} alt="Elderly person and young adult enjoying time together" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a3329]/90 via-[#2d4a3e]/75 to-[#2d4a3e]/30" />
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/20 text-accent text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              {t("hero_badge")}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              {t("hero_title")}
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl font-light leading-relaxed">
              {t("hero_subtitle")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button variant="hero" size="lg" className="text-base px-8 py-6 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary" asChild>
              <Link to="/order" onClick={() => trackCTAClick("hero_cta")}>
                {t("hero_cta")}
                <ArrowRight className="w-5 h-5 ml-1" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="text-base px-8 py-6 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              asChild
            >
              <a href="#how-it-works" onClick={() => trackCTAClick("hero_secondary")}>
                {t("hero_secondary")}
              </a>
            </Button>
            <Button
              variant="hero"
              size="lg"
              className="text-base px-8 py-6 bg-white text-primary hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              asChild
            >
              <Link to="/order" onClick={() => trackCTAClick("hero_book_service")}>
                <CalendarCheck className="w-5 h-5 mr-1" />
                {t("hero_book_service")}
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-10 flex items-center gap-6 text-white/70 text-sm"
          >
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t("hero_countries")}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t("hero_families")}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t("hero_caregivers")}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
